import SendIcon from '@mui/icons-material/Send'
import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    MenuItem,
    Paper,
    Select,
    Step,
    StepLabel,
    Stepper,
    Tooltip,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import useCommsManager from '../../hooks/useCommsManager'
import axiosInstance from '../../services/axios/axiosConfig'
import { endpoints } from '../../services/axios/endpoints'
import ErrorBanner from '../../components/shared/ErrorBanner'
import PipelineService from '../../services/pipelineService'
import BulkSendService from '../../services/bulkSendService'
import PreSendReport from './PreSendReport'
import { parseCSV } from '../../utils/csv'
import { previewBody } from '../../helpers/templateDownload'
import {
    buildBulkLog,
    buildBulkPayload,
    contentForEvent,
    eventDetailsFrom,
    identityColumns,
    missingIdentities,
    missingVariables,
    reportIdFor,
    requiredIdentities,
    selectBulkRows,
} from '../../helpers/bulkSend'
import {
    activeMappings,
    canLinkVariables,
    sessionIdError,
    sessionIdToSend,
} from '../../helpers/linkVariables'
import {
    INDIA_ZONE,
    formatIndiaTime,
    formatInZone,
    sendTiming,
} from '../../helpers/sendSchedule'
import { errorMessage } from '../../services/axios/errorMessage'
import TemplateService from '../../services/templateService'
import { nowUnix } from '../../utils/datetime'
import {
    BRAND,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    TEXT,
    cardSx,
    pageSx,
} from '../../theme/tokens'
import CsvPreview from './CsvPreview'
import EventPicker from './EventPicker'
import EventRequirements from './EventRequirements'
import FileDropZone from './FileDropZone'
import LinkVariables from './LinkVariables'
import Overline from './Overline'
import ReviewTile from './ReviewTile'
import ScheduledSends from './ScheduledSends'
import SendAtPicker from './SendAtPicker'
import SendResult from './SendResult'
import ShortLinks from './ShortLinks'

const STEPS = ['Select Event', 'Upload Recipients', 'Review & Send']

const NO_DETAILS = { loading: false, error: '', data: null }
const NO_SCHEDULE = { date: '', time: '', zone: INDIA_ZONE }
const NO_CONTENT = { loading: false, channels: [] }

const recipientsWord = (n) => `recipient${n === 1 ? '' : 's'}`

/** The email and mobile columns, guessed from their header names. */
const guessIdentityColumns = (headers) => ({
    email: headers.find((h) => h.toLowerCase().includes('email')),
    mobile: headers.find(
        (h) =>
            h.toLowerCase().includes('mobile') ||
            h.toLowerCase().includes('phone')
    ),
})

/** A labelled select of the file's columns (or None), for the email or the mobile column. */
const ColumnPicker = ({ label, headers, value, onChange }) => (
    <>
        <Overline component="label" sx={{ mb: 0.5 }}>
            {label}
        </Overline>
        <Select
            fullWidth
            size="small"
            value={value}
            displayEmpty
            inputProps={{ 'aria-label': label }}
            onChange={(e) => onChange(e.target.value)}
        >
            <MenuItem value="">
                <em>None</em>
            </MenuItem>
            {headers.map((h) => (
                <MenuItem key={h} value={h}>
                    {h}
                </MenuItem>
            ))}
        </Select>
    </>
)

ColumnPicker.propTypes = {
    label: PropTypes.string.isRequired,
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
}

/**
 * Bulk Send: pick an event, upload its recipients, review and send, plus the
 * event's scheduled sends below.
 *
 * Step 1 loads what the event needs (`/comms/v2/pipelines/event_type`):
 * whether it sends at a chosen time (then "Send at" is asked for), whether it
 * keeps to daytime (niceness), the columns and template variables the file
 * must have. Step 2 checks the file against them, offers linked variables and
 * short links. Step 3 runs the pre-send report and sends.
 */
const BulkSend = () => {
    // Sending needs the comms manager role, as in the old app; others can
    // still prepare a send and read its report.
    const { note: manageNote } = useCommsManager()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { eventTypes } = useSelector((state) => state.pipeline)
    const { templates } = useSelector((state) => state.template)

    const [step, setStep] = useState(0)
    // Server's pre-send verdict. null = report unavailable, which must NOT
    // block: refusing to send because we could not describe the send would be
    // worse than sending.
    const [verdict, setVerdict] = useState(null)
    // True while the pre-send report runs: Send waits for its verdict.
    const [reportRunning, setReportRunning] = useState(false)
    const [eventType, setEventType] = useState('')
    // What the event needs: {loading, error, data: eventDetailsFrom(...)}.
    const [details, setDetails] = useState(NO_DETAILS)
    const [schedule, setSchedule] = useState(NO_SCHEDULE)
    const [csvData, setCsvData] = useState(null)
    const [emailColumn, setEmailColumn] = useState('')
    const [mobileColumn, setMobileColumn] = useState('')
    const [fileName, setFileName] = useState('')
    // Bumped for every new or cleared file, so per-file sections start over.
    const [fileVersion, setFileVersion] = useState(0)
    const [mapping, setMapping] = useState({})
    const [sessionId, setSessionId] = useState('')
    // The short link result in use ({rows, columns, failedRows, total}), or null.
    const [shortLinks, setShortLinks] = useState(null)
    const [shortLinksRunning, setShortLinksRunning] = useState(false)
    const [sending, setSending] = useState(false)
    const [sendError, setSendError] = useState('')
    const [result, setResult] = useState(null)
    // What the pre-send report checks, resolved from the event's active
    // pipelines when the review step opens: {loading, channels, subject,
    // templateBody, error}. It used to send an empty subject and body, which
    // the report fails ("Subject is empty"), so Send was always disabled.
    const [content, setContent] = useState(NO_CONTENT)
    // One id per review, used by both the bulk log and the send: the Report ID.
    const [eventId, setEventId] = useState('')
    // The event whose details may land; an older request's answer is dropped.
    const detailsFor = useRef('')

    useEffect(() => {
        dispatch(PipelineService.getEventTypes())
        if (!templates?.length) dispatch(TemplateService.getTemplates())
    }, [])

    const loadDetails = useCallback(async (type) => {
        detailsFor.current = type
        if (!type) {
            setDetails(NO_DETAILS)
            return
        }
        setDetails({ loading: true, error: '', data: null })
        try {
            const res = await BulkSendService.eventDetails(type)
            if (detailsFor.current === type)
                setDetails({
                    loading: false,
                    error: '',
                    data: eventDetailsFrom(res.data),
                })
        } catch (err) {
            if (detailsFor.current === type)
                setDetails({
                    loading: false,
                    error: errorMessage(err.response),
                    data: null,
                })
        }
    }, [])

    // A new event brings its own variables and timing: choices made for the
    // previous one do not carry over.
    useEffect(() => {
        loadDetails(eventType)
        setMapping({})
        setSessionId('')
        setSchedule(NO_SCHEDULE)
    }, [eventType, loadDetails])

    useEffect(() => {
        if (step !== 2 || !eventType) return
        setEventId(reportIdFor(eventType, nowUnix()))
        setContent({ loading: true, channels: [] })
        let cancelled = false
        const load = async () => {
            try {
                const resp = await axiosInstance.get(
                    endpoints.getPipelines(eventType)
                )
                const found = contentForEvent(resp.data ?? [], templates ?? [])
                let templateBody = ''
                if (found.emailTemplateId) {
                    const preview = await axiosInstance.get(
                        endpoints.getTemplateData(found.emailTemplateId),
                        { silentToast: true }
                    )
                    templateBody = previewBody(preview.data?.response)
                }
                if (!cancelled)
                    setContent({ loading: false, ...found, templateBody })
            } catch (err) {
                if (!cancelled)
                    setContent({
                        loading: false,
                        channels: [],
                        error: errorMessage(err.response),
                    })
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [step, eventType])

    /** Take a parsed file: it replaces the previous one, columns guessed again. */
    const acceptParsed = (parsed, name) => {
        setCsvData(parsed)
        setFileName(name)
        setShortLinks(null)
        setFileVersion((v) => v + 1)
        const guessed = guessIdentityColumns(parsed.headers)
        setEmailColumn(guessed.email ?? '')
        setMobileColumn(guessed.mobile ?? '')
    }

    const clearFile = () => {
        setCsvData(null)
        setFileName('')
        setShortLinks(null)
        setFileVersion((v) => v + 1)
        setEmailColumn('')
        setMobileColumn('')
    }

    // Recipients handed over from another page (CSV Validator, CRM Enrich...).
    useEffect(() => {
        const state = location.state
        if (!state) return
        if (state.csvText) {
            acceptParsed(parseCSV(state.csvText), 'pre-loaded.csv')
        } else if (Array.isArray(state.contacts) && state.contacts.length > 0) {
            acceptParsed(
                {
                    headers: Object.keys(state.contacts[0]),
                    rows: state.contacts,
                },
                'contacts.csv'
            )
        }
    }, [])

    /** Read a picked or dropped file. */
    const loadFile = (file) => {
        const reader = new FileReader()
        reader.onload = (ev) =>
            acceptParsed(parseCSV(ev.target.result), file.name)
        reader.readAsText(file)
    }

    const eventData = details.data
    const variables = useMemo(() => eventData?.variables ?? [], [eventData])
    const linkable = canLinkVariables(eventData)
    // Only real links of this event's variables, and none when the event
    // cannot link (no inline receiver).
    const mapped = useMemo(
        () => (linkable ? activeMappings(mapping, variables) : {}),
        [linkable, mapping, variables]
    )
    const headers = csvData?.headers ?? []
    // Shortened rows once short links are made; the file's rows otherwise.
    const sendRows = shortLinks?.rows ?? csvData?.rows ?? []

    // Columns named after another identity (lead_id, batch_id...), sent as
    // identities for parent and batch-student pipelines.
    const otherIdentityColumns = useMemo(
        () => identityColumns(headers, { emailColumn, mobileColumn }),
        [csvData, emailColumn, mobileColumn]
    )
    const selection = useMemo(
        () =>
            selectBulkRows({
                rows: sendRows,
                emailColumn,
                mobileColumn,
                otherIdentityColumns,
            }),
        [sendRows, emailColumn, mobileColumn, otherIdentityColumns]
    )
    // What the event's pipelines need that the file does not have; the API
    // refuses the whole send with "required identity not found". Checked on
    // the upload step from the event's details, and again on review from the
    // pipelines the report reads.
    const uploadMissingIdentities = eventData
        ? missingIdentities(requiredIdentities(eventData.comms), {
              headers,
              emailColumn,
              mobileColumn,
          })
        : []
    const missingColumns = missingIdentities(
        requiredIdentities(content.comms ?? []),
        { headers, emailColumn, mobileColumn }
    )
    // Without a column a template variable would go out blank.
    const missingVars = missingVariables(variables, { headers, mapped })
    const sessionError = sessionIdError(mapped, sessionId)
    const hasRecipientColumn =
        !!emailColumn || !!mobileColumn || otherIdentityColumns.length > 0
    // One recipient per kept row (a row's email and mobile are one person;
    // they used to be counted twice).
    const totalRecipients = selection.rows.length

    // When it goes out, and whether it may (a chosen moment ahead, niceness).
    const timing = sendTiming({
        absoluteTime: !!eventData?.absoluteTime,
        niceness: !!eventData?.niceness,
        schedule,
        nowSeconds: nowUnix(),
    })
    const scheduledEpoch = timing.scheduled ? timing.epoch : null

    const payload = useMemo(
        () =>
            buildBulkPayload({
                eventType,
                eventId,
                rows: selection.rows,
                headers,
                emailColumn,
                mobileColumn,
                timezone: timing.zone || INDIA_ZONE,
                eventTime: scheduledEpoch ?? '',
                mappedOthers: mapped,
                sessionId: sessionIdToSend(mapped, sessionId),
                variables,
            }),
        [
            eventType,
            eventId,
            selection,
            csvData,
            emailColumn,
            mobileColumn,
            timing.zone,
            scheduledEpoch,
            mapped,
            sessionId,
            variables,
        ]
    )

    const noPipelines =
        step === 2 &&
        !content.loading &&
        !content.error &&
        !content.channels.length

    const onSend = async () => {
        if (!hasRecipientColumn || totalRecipients === 0) {
            setSendError('Select a column with at least one recipient')
            return
        }
        if (missingColumns.length) {
            setSendError(
                `This event's pipelines need ${missingColumns.join(
                    '; '
                )} in the CSV.`
            )
            return
        }
        // Again at the moment of sending: the page may have been open a while.
        const now = nowUnix()
        const latest = sendTiming({
            absoluteTime: !!eventData?.absoluteTime,
            niceness: !!eventData?.niceness,
            schedule,
            nowSeconds: now,
        })
        if (latest.error) {
            setSendError(latest.error)
            return
        }
        setSendError('')
        setSending(true)
        try {
            // The send API looks the event_id up in the bulk logs first.
            await axiosInstance.post(
                endpoints.createCommBulkLog,
                buildBulkLog({
                    eventType,
                    eventId,
                    eventTime: latest.scheduled ? latest.epoch : now,
                    shortLinkColumns: shortLinks?.columns ?? [],
                })
            )
            await axiosInstance.post(endpoints.bulkSendComms, payload)
            setResult({
                success: true,
                total: totalRecipients,
                ...(latest.scheduled
                    ? { scheduledFor: latest.epoch, zone: latest.zone }
                    : {}),
            })
        } catch (err) {
            setResult({ success: false, reason: errorMessage(err.response) })
        } finally {
            setSending(false)
        }
    }

    const startOver = () => {
        setResult(null)
        setStep(0)
        setEventType('')
        clearFile()
        setSendError('')
        setVerdict(null)
        setContent(NO_CONTENT)
        setEventId('')
    }

    const canProceedStep0 =
        !!eventType &&
        !!eventData &&
        !details.loading &&
        !(timing.scheduled && timing.error)
    const canProceedStep1 =
        !!csvData &&
        sendRows.length > 0 &&
        hasRecipientColumn &&
        !uploadMissingIdentities.length &&
        !missingVars.length &&
        !sessionError &&
        !shortLinksRunning

    const previewRows = sendRows.slice(0, 5)

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* Page Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 5,
                    }}
                >
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: `${RADIUS.md}px`,
                            background: GRADIENT.brand,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <SendIcon
                            sx={{ color: ON_ORANGE, fontSize: ICON.xl }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                color: TEXT.primary,
                                lineHeight: 1.2,
                            }}
                        >
                            Bulk Send
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.faint, mt: 0.3 }}
                        >
                            Trigger communications for a list of recipients via
                            CSV upload
                        </Typography>
                    </Box>
                </Box>

                {/* Stepper: colours come from the dark-native theme */}
                <Stepper activeStep={step} sx={{ mb: 4 }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {result ? (
                    <SendResult
                        result={result}
                        reportId={eventId}
                        onViewReport={() =>
                            // The id travels in the address too, so the
                            // report can be reloaded or its link shared.
                            navigate(
                                {
                                    pathname: '/reports',
                                    search: `?reportId=${encodeURIComponent(
                                        eventId
                                    )}`,
                                },
                                { state: { reportId: eventId } }
                            )
                        }
                        onSendAnother={startOver}
                        onBack={() => navigate('/pipelines')}
                    />
                ) : (
                    <>
                        {step === 0 && (
                            <Paper elevation={0} sx={{ ...cardSx, p: 4 }}>
                                <Overline component="h2" sx={{ mb: 0.5 }}>
                                    Event Type
                                </Overline>
                                <Typography
                                    variant="body2"
                                    sx={{ color: TEXT.faint, mb: 3 }}
                                >
                                    All active pipelines configured for this
                                    event type will be triggered for each
                                    recipient.
                                </Typography>
                                <EventPicker
                                    id="bulk-send-event-type"
                                    label="Event Type"
                                    eventTypes={eventTypes}
                                    value={eventType}
                                    onChange={setEventType}
                                />
                                {eventType && (
                                    <EventRequirements
                                        eventType={eventType}
                                        details={details}
                                        onRetry={() => loadDetails(eventType)}
                                    />
                                )}
                                {eventData?.absoluteTime && (
                                    <SendAtPicker
                                        value={schedule}
                                        onChange={(patch) =>
                                            setSchedule((s) => ({
                                                ...s,
                                                ...patch,
                                            }))
                                        }
                                        check={timing}
                                    />
                                )}
                            </Paper>
                        )}

                        {step === 1 && (
                            <Paper elevation={0} sx={{ ...cardSx, p: 4 }}>
                                <Overline component="h2" sx={{ mb: 0.5 }}>
                                    Upload Recipients
                                </Overline>
                                <Typography
                                    variant="body2"
                                    sx={{ color: TEXT.faint, mb: 3 }}
                                >
                                    CSV must have a header row. Map columns to
                                    email and/or mobile number below.
                                </Typography>

                                <FileDropZone
                                    fileName={fileName}
                                    onFile={loadFile}
                                    onClear={clearFile}
                                />

                                {csvData && (
                                    <>
                                        <Alert
                                            severity="success"
                                            sx={{ mb: 3 }}
                                        >
                                            {csvData.rows.length} row
                                            {csvData.rows.length === 1
                                                ? ''
                                                : 's'}{' '}
                                            loaded from {fileName}
                                        </Alert>
                                        <Grid
                                            container
                                            spacing={2}
                                            sx={{ mb: 3 }}
                                        >
                                            <Grid item xs={12} sm={6}>
                                                <ColumnPicker
                                                    label="Email column (optional)"
                                                    headers={headers}
                                                    value={emailColumn}
                                                    onChange={setEmailColumn}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <ColumnPicker
                                                    label="Mobile number column (optional)"
                                                    headers={headers}
                                                    value={mobileColumn}
                                                    onChange={setMobileColumn}
                                                />
                                            </Grid>
                                        </Grid>
                                        {otherIdentityColumns.length > 0 && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: TEXT.secondary,
                                                    mt: -1.5,
                                                    mb: 3,
                                                }}
                                            >
                                                Also sent as recipient
                                                identities:{' '}
                                                {otherIdentityColumns.join(
                                                    ', '
                                                )}
                                            </Typography>
                                        )}

                                        {linkable && (
                                            <LinkVariables
                                                variables={variables}
                                                mapping={mapping}
                                                mapped={mapped}
                                                onMappingChange={(
                                                    variable,
                                                    value
                                                ) =>
                                                    setMapping((m) => ({
                                                        ...m,
                                                        [variable]: value,
                                                    }))
                                                }
                                                sessionId={sessionId}
                                                onSessionIdChange={setSessionId}
                                                sessionError={sessionError}
                                            />
                                        )}

                                        <ShortLinks
                                            key={fileVersion}
                                            headers={headers}
                                            rows={csvData.rows}
                                            result={shortLinks}
                                            onResult={setShortLinks}
                                            onRunningChange={
                                                setShortLinksRunning
                                            }
                                            downloadName={`${
                                                fileName.replace(
                                                    /\.csv$/i,
                                                    ''
                                                ) || 'recipients'
                                            }_processed.csv`}
                                        />

                                        <Overline sx={{ mb: 1 }}>
                                            {shortLinks
                                                ? 'Preview (first 5 rows, with short links)'
                                                : 'Preview (first 5 rows)'}
                                        </Overline>
                                        <CsvPreview
                                            headers={headers}
                                            rows={previewRows}
                                            emailColumn={emailColumn}
                                            mobileColumn={mobileColumn}
                                        />

                                        {uploadMissingIdentities.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <ErrorBanner
                                                    message={`This event's pipelines need ${uploadMissingIdentities.join(
                                                        '; '
                                                    )}. Pick the email or mobile column above, or add the column to the file.`}
                                                />
                                            </Box>
                                        )}
                                        {missingVars.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <ErrorBanner
                                                    message={`These template variables have no column: ${missingVars.join(
                                                        ', '
                                                    )}. ${
                                                        linkable
                                                            ? 'Add the columns to the file, or link them above.'
                                                            : 'Add the columns to the file.'
                                                    }`}
                                                />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Paper>
                        )}

                        {step === 2 && csvData && (
                            <Paper elevation={0} sx={{ ...cardSx, p: 4 }}>
                                <Overline component="h2" sx={{ mb: 3 }}>
                                    Review before sending
                                </Overline>
                                {/* Full pre-send report: audience arithmetic,
                                    warmup position for the sending domain and
                                    the deliverability checklist. Built from the
                                    same payload the send will use, so the
                                    recipient count cannot drift from it. */}
                                {content.loading && (
                                    <Typography
                                        role="status"
                                        variant="body2"
                                        sx={{ color: TEXT.muted, mb: 2 }}
                                    >
                                        Loading the pipelines and template for
                                        this event…
                                    </Typography>
                                )}
                                {content.error && (
                                    <Box sx={{ mb: 2 }}>
                                        <ErrorBanner
                                            message={`Could not load this event's pipelines: ${content.error}`}
                                        />
                                    </Box>
                                )}
                                {!content.loading && missingColumns.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <ErrorBanner
                                            message={`This event's pipelines need ${missingColumns.join(
                                                '; '
                                            )} in the CSV, as a column of that name. Add it and upload again.`}
                                        />
                                    </Box>
                                )}
                                {noPipelines && (
                                    <Box sx={{ mb: 2 }}>
                                        <ErrorBanner
                                            message={`No active pipeline is set up for ${eventType}, so nothing would be sent. Create one under Pipelines first.`}
                                        />
                                    </Box>
                                )}
                                {timing.error && (
                                    <Box sx={{ mb: 2 }}>
                                        <ErrorBanner message={timing.error} />
                                    </Box>
                                )}
                                {/* Only once the event's channels and template are
                                    known: the report re-runs on subject/body, not
                                    on channels, so an early run would stick. */}
                                {!content.loading &&
                                    !content.error &&
                                    content.channels.length > 0 && (
                                        <Box sx={{ mb: 3 }}>
                                            <PreSendReport
                                                eventType={eventType}
                                                eventId=""
                                                identities={payload.identities}
                                                others={payload.others}
                                                sender=""
                                                templateBody={
                                                    content.templateBody ?? ''
                                                }
                                                subject={content.subject ?? ''}
                                                channels={content.channels}
                                                onVerdict={setVerdict}
                                                onRunning={setReportRunning}
                                                timezone={payload.timezone}
                                                eventTime={payload.event_time}
                                                mappedOthers={
                                                    payload.mapped_others
                                                }
                                                sessionId={payload.session_id}
                                            />
                                        </Box>
                                    )}
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={4}>
                                        <ReviewTile
                                            label="Event Type"
                                            value={eventType}
                                            accent={BRAND.orange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <ReviewTile
                                            label="Unique Recipients"
                                            value={totalRecipients}
                                            accent={STATUS.success.fg}
                                            valueColor={STATUS.success.fg}
                                            large
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <ReviewTile
                                            label="Source File"
                                            value={fileName}
                                            accent={STATUS.warning.fg}
                                        />
                                    </Grid>
                                </Grid>
                                {timing.scheduled && timing.epoch ? (
                                    <Alert severity="info">
                                        Scheduled for{' '}
                                        {formatInZone(
                                            timing.epoch,
                                            timing.zone
                                        )}{' '}
                                        ({timing.zone})
                                        {timing.zone === INDIA_ZONE
                                            ? ''
                                            : `, which is ${formatIndiaTime(
                                                  timing.epoch
                                              )}`}
                                        . All {totalRecipients} unique{' '}
                                        {recipientsWord(totalRecipients)} get it
                                        then. Until that time you can cancel it
                                        under Scheduled sends below.
                                    </Alert>
                                ) : (
                                    <Alert severity="warning">
                                        This will immediately queue
                                        communications for all {totalRecipients}{' '}
                                        unique {recipientsWord(totalRecipients)}
                                        . This action cannot be undone.
                                    </Alert>
                                )}
                                {sendError && (
                                    <Box sx={{ mt: 2 }}>
                                        <ErrorBanner message={sendError} />
                                    </Box>
                                )}
                            </Paper>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 3,
                            }}
                        >
                            <Button
                                variant="outlined"
                                disabled={step === 0}
                                onClick={() => setStep((s) => s - 1)}
                            >
                                Back
                            </Button>
                            {step < 2 ? (
                                <Button
                                    variant="contained"
                                    disabled={
                                        step === 0
                                            ? !canProceedStep0
                                            : !canProceedStep1
                                    }
                                    onClick={() => setStep((s) => s + 1)}
                                >
                                    Continue
                                </Button>
                            ) : (
                                <Tooltip title={manageNote}>
                                    {/* A disabled button gets no hover; the span does. */}
                                    <span>
                                        <Button
                                            variant="contained"
                                            // Blocked only when the server says so,
                                            // or the timing does. A missing report
                                            // (verdict null) still allows the send;
                                            // one still running holds it.
                                            color="success"
                                            disabled={
                                                Boolean(manageNote) ||
                                                sending ||
                                                content.loading ||
                                                reportRunning ||
                                                noPipelines ||
                                                !!timing.error ||
                                                (verdict !== null &&
                                                    verdict.can_send === false)
                                            }
                                            startIcon={<SendIcon />}
                                            onClick={onSend}
                                            // Colours from the theme's success
                                            // button (Deep Blue on green, 6.8:1).
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {sending
                                                ? 'Sending…'
                                                : `${
                                                      timing.scheduled
                                                          ? 'Schedule for'
                                                          : 'Send to'
                                                  } ${totalRecipients} ${recipientsWord(
                                                      totalRecipients
                                                  )}`}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                    </>
                )}

                <ScheduledSends eventTypes={eventTypes} />
            </Box>
        </Box>
    )
}

export default BulkSend
